/**
 * DAA alerts store (ENG-340) — live ownship/intruder encounters from the
 * `daa_alerts` topic, plus a capped log of the moments worth reading.
 *
 * The topic fires ~4 Hz per pair, so the list is one row per pair (the latest
 * message overwrites), and the log only records when an encounter starts,
 * escalates or ends — that log is what the sidebar's unread counter counts.
 * ENG-345 draws its map lines from `byKey`.
 *
 * Expiry follows the BE's guidance: a pair that goes quiet for 12 s is dropped
 * (not every encounter ends with a `resolved` message), and a resolved pair is
 * kept for 15 s so it can fade out.
 */
import type { DaaAlertEnvelope, DaaLevel } from '@/lib/mqtt/schemas/daaAlerts';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

export const DAA_EXPIRY_MS = 12_000;
export const DAA_RESOLVED_KEEP_MS = 15_000;
/** The simulated encounters start ~50 pairs at once, so 100 filled in about a
 *  minute; 500 holds several minutes. In-memory only: a reload empties it. */
const LOG_CAP = 500;

export interface DaaEncounter extends DaaAlertEnvelope {
  firstSeenAt: number;
  lastSeenAt: number;
  resolvedAt: number | null;
  peakLevel: DaaLevel;
}

export type DaaLogKind = 'started' | 'escalated' | 'resolved';

export interface DaaLogEntry {
  id: string;
  at: number;
  kind: DaaLogKind;
  key: string;
  level: DaaLevel;
  intruderLabel: string;
  rangeM: number | null;
  verticalSepM: number | null;
  advisory: string | null;
}

interface DaaAlertsStore {
  byKey: Record<string, DaaEncounter>;
  /** Newest first. */
  log: DaaLogEntry[];
  apply: (env: DaaAlertEnvelope, now?: number) => void;
  prune: (now?: number) => void;
  clear: () => void;
}

const logEntry = (kind: DaaLogKind, e: DaaEncounter, at: number): DaaLogEntry => ({
  id: `${e.key}:${at}:${kind}`,
  at,
  kind,
  key: e.key,
  level: e.level,
  intruderLabel: e.intruderLabel,
  rangeM: e.rangeM,
  verticalSepM: e.verticalSepM,
  advisory: e.advisory,
});

const pushLog = (log: DaaLogEntry[], entry: DaaLogEntry) => [entry, ...log].slice(0, LOG_CAP);

export const useDaaAlertsStore = create<DaaAlertsStore>()(
  devtools(
    subscribeWithSelector((set) => ({
      byKey: {},
      log: [],
      apply: (env, now = Date.now()) =>
        set((s) => {
          const prev = s.byKey[env.key];

          if (env.status === 'resolved') {
            // Nothing to resolve, or already resolved: ignore the repeat.
            if (!prev || prev.status === 'resolved') return s;
            const next: DaaEncounter = { ...prev, ...env, lastSeenAt: now, resolvedAt: now };
            return {
              byKey: { ...s.byKey, [env.key]: next },
              log: pushLog(s.log, logEntry('resolved', next, now)),
            };
          }

          // Level 0 is "not an alert" — never opens an encounter on its own.
          if (!prev && env.level === 0) return s;

          const restarted = prev?.status === 'resolved';
          const base = prev && !restarted ? prev : null;
          const next: DaaEncounter = {
            ...env,
            firstSeenAt: base?.firstSeenAt ?? now,
            lastSeenAt: now,
            resolvedAt: null,
            peakLevel: Math.max(base?.peakLevel ?? 0, env.level) as DaaLevel,
          };

          let log = s.log;
          if (!base && env.level >= 1) log = pushLog(log, logEntry('started', next, now));
          else if (base && env.level > base.peakLevel && env.level >= 1) {
            log = pushLog(log, logEntry('escalated', next, now));
          }
          return { byKey: { ...s.byKey, [env.key]: next }, log };
        }),
      prune: (now = Date.now()) =>
        set((s) => {
          let log = s.log;
          let byKey: Record<string, DaaEncounter> | null = null;
          for (const [key, e] of Object.entries(s.byKey)) {
            const gone =
              e.status === 'resolved'
                ? now - (e.resolvedAt ?? e.lastSeenAt) > DAA_RESOLVED_KEEP_MS
                : now - e.lastSeenAt > DAA_EXPIRY_MS;
            if (!gone) continue;
            byKey ??= { ...s.byKey };
            delete byKey[key];
            // Quiet for too long without a `resolved`: the encounter is over.
            if (e.status === 'active') log = pushLog(log, logEntry('resolved', e, now));
          }
          return byKey ? { byKey, log } : s;
        }),
      clear: () => set({ byKey: {}, log: [] }),
    })),
    { name: 'daaAlerts' },
  ),
);
